import { getFirebaseConfig } from '../config'
import firebaseStart, { getFirebaseApp } from '../services/firebase'
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth'
import { useContext, useEffect } from 'react'
import { AuthContext } from '../contexts/auth-context'

function useAuth() {
  const firebaseConfig = getFirebaseConfig()
  let firebaseApp = getFirebaseApp()

  if (!firebaseApp) {
    firebaseStart()
    firebaseApp = getFirebaseApp()
  }

  const auth = getAuth(firebaseApp)
  auth.tenantId = firebaseConfig.firebaseTenant

  const {
    isAuthenticated,
    idToken,
    setIsAuthenticated,
    setIdToken,
  } = useContext(AuthContext)

  // Check for existing authentication state on hook initialization
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log('onAuthStateChanged::: Signed in user', user.uid)
        const token = await user.getIdToken()
        setIdToken(token)
        setIsAuthenticated(true)
      } else {
        console.log('onAuthStateChanged::: No signed in user')
        setIsAuthenticated(false)
        setIdToken(null)
      }
    })

    return () => unsubscribe()
  }, [auth, setIsAuthenticated, setIdToken])

  useEffect(() => {
    let tokenRefreshInterval: NodeJS.Timeout;

    if (isAuthenticated && auth.currentUser) {
      tokenRefreshInterval = setInterval(async () => {
        try {
          console.log('Refreshing token...');
          const freshToken = await auth.currentUser!.getIdToken(true);
          setIdToken(freshToken);
        } catch (error) {
          console.error('Failed to refresh token:', error);
        }
      }, 50 * 60 * 1000);
    }

    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [isAuthenticated, auth, setIdToken]);

  async function login() {
    // Only sign in anonymously if no user is currently authenticated
    if (!auth.currentUser) {
      console.log('login:: No current user, signing in anonymously')
      await signInAnonymously(auth)
    }

    if (auth.currentUser) {
      console.log('login:: Getting token')
      const token = await auth.currentUser.getIdToken()
      setIdToken(token)
      setIsAuthenticated(true)
    }
  }

  return {
    isAuthenticated,
    login,
    idToken,
  }
}

export default useAuth
